import { useMemo } from 'react'
import { NavLink } from 'react-router-dom'

import AutoDeploymentIcon from '@/assets/icons/auto-deployment.svg?react'
// import AutoScalingIcon from '@/assets/icons/auto-scaling.svg?react'
import BillingIcon from '@/assets/icons/billing.svg?react'
import CreateNewIcon from '@/assets/icons/create-new.svg?react'
import DataListIcon from '@/assets/icons/data-list.svg?react'
import HomeIcon from '@/assets/icons/home.svg?react'
import InstanceListIcon from '@/assets/icons/instance-list.svg?react'
import MemberIcon from '@/assets/icons/member.svg?react'
import NetworkEndpointIcon from '@/assets/icons/network-endpoint.svg?react'
import PermissionQuotaIcon from '@/assets/icons/permissionQuota.svg?react'
import PlatformSettingIcon from '@/assets/icons/platform-setting.svg?react'
import SnapListIcon from '@/assets/icons/snapshot-list.svg?react'
import SpaceMgrIcon from '@/assets/icons/space-mgr.svg?react'
import TeamsIcon from '@/assets/icons/teams.svg?react'
import { TeamRole } from '@/consts/team'
import { useTeamUserRole } from '@/stores/team'
import { cn } from '@/utils/style'

import { Trans } from './Trans'

interface MenuItem {
  path: string
  icon: React.ComponentType<{ className?: string }>
  labelKey: string
  defaultLabel: string
  allowedRoles?: TeamRole[] // undefined = all roles can access
  end?: boolean // false = match child routes, true/undefined = exact match
  dividerAfter?: boolean // true = show divider line after this item
}

const MENU_ITEMS: MenuItem[] = [
  {
    path: '/team/home',
    icon: HomeIcon,
    labelKey: 'layout.menuHome',
    defaultLabel: 'Home',
    dividerAfter: true,
  },
  {
    path: '/team/create',
    icon: CreateNewIcon,
    labelKey: 'create.createNew',
    defaultLabel: 'Create New',
  },
  {
    path: '/team/instances',
    icon: InstanceListIcon,
    labelKey: 'layout.menuTeamInstances',
    defaultLabel: 'Instances',
  },
  {
    path: '/team/deploy',
    icon: AutoDeploymentIcon,
    labelKey: 'layout.menuAutoDeployment',
    defaultLabel: 'Auto Deploy',
  },
  {
    path: '/team/data',
    icon: DataListIcon,
    labelKey: 'layout.menuDataMgr',
    defaultLabel: 'Datadrive',
  },
  {
    path: '/team/snapshots',
    icon: SnapListIcon,
    labelKey: 'layout.menuSnapshots',
    defaultLabel: 'Snapshots',
    end: false,
  },
  // {
  //   path: '/team/scaling',
  //   icon: AutoScalingIcon,
  //   labelKey: 'layout.menuAutoScaling',
  //   defaultLabel: 'Auto Scaling',
  // },
  {
    path: '/team/space',
    icon: SpaceMgrIcon,
    labelKey: 'layout.menuSpace',
    defaultLabel: 'Storage Space',
    end: false,
  },
  {
    path: '/team/eip',
    icon: NetworkEndpointIcon,
    labelKey: 'layout.menuNetworkEndpoint',
    defaultLabel: 'Network Endpoint',
    dividerAfter: true,
  },
  {
    path: '/team/billing',
    icon: BillingIcon,
    labelKey: 'layout.menuBilling',
    defaultLabel: 'Platform Billing',
  },
  {
    path: '/team/profile',
    icon: PlatformSettingIcon,
    labelKey: 'layout.menuProfile',
    defaultLabel: 'Platform Setting',
  },
  {
    path: '/team/member',
    icon: MemberIcon,
    labelKey: 'layout.menuMember',
    defaultLabel: 'Member',
    allowedRoles: [TeamRole.Owner, TeamRole.Admin],
  },
  {
    path: '/team/permission-quota',
    icon: PermissionQuotaIcon,
    labelKey: 'layout.menuPermissionQuota',
    defaultLabel: 'Permissions & Quota',
    allowedRoles: [TeamRole.Owner, TeamRole.Admin],
  },
  {
    path: '/team/setting',
    icon: TeamsIcon,
    labelKey: 'layout.menuTeamSetting',
    defaultLabel: 'Team Setting',
    allowedRoles: [TeamRole.Owner, TeamRole.Admin],
  },
]

export function TeamMenu() {
  const userRole = useTeamUserRole() || TeamRole.Normal

  const visibleMenuItems = useMemo(
    () => MENU_ITEMS.filter((item) => !item.allowedRoles || item.allowedRoles.includes(userRole)),
    [userRole],
  )

  return (
    <div className="card p-4 bg-panel-white w-[240px] flex flex-shrink-0 flex-col justify-between gap-10 sticky top-0 overflow-y-auto">
      <div>
        <div>
          {visibleMenuItems.map((item) => (
            <div key={item.path}>
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) => cn('menu-link', { active: isActive })}
              >
                <item.icon className="w-5 h-5 text-tertiary" />
                <Trans i18nKey={item.labelKey}>{item.defaultLabel}</Trans>
              </NavLink>
              {item.dividerAfter && <div className="h-px bg-neutral-200 m-4" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
